import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // General Questions
  {
    category: "General",
    question: "What is a PIN code?",
    answer: "A PIN (Postal Index Number) code is a 6-digit number that uniquely identifies postal delivery areas in India. The first digit represents the region, the second the sub-region, and the last four digits represent the specific delivery post office."
  },
  {
    category: "General",
    question: "How do I find my area's PIN code?",
    answer: "You can find your area's PIN code by using our search feature. Simply enter your locality name, city, or a nearby landmark in the search box, and we'll show you the corresponding PIN code and post office details."
  },
  {
    category: "General",
    question: "Are all PIN codes 6 digits long?",
    answer: "Yes, all valid Indian PIN codes are exactly 6 digits long. This standardization helps in efficient mail sorting and delivery across the country."
  },
  {
    category: "General",
    question: "What does each digit in a PIN code mean?",
    answer: "In a 6-digit PIN code: First digit (1-9) represents the region, second digit indicates the sub-region, third digit represents the sorting district, and the last three digits represent the specific post office."
  },
  // Search Related
  {
    category: "Search",
    question: "How accurate is the PIN code search?",
    answer: "Our PIN code database is synchronized daily with India Post's official records, ensuring high accuracy. However, for the most current information, we recommend verifying critical details with your local post office."
  },
  {
    category: "Search",
    question: "Why can't I find my area in the search results?",
    answer: "This could be due to: 1) A different spelling variation of your area name, 2) The area being served by a nearby post office, or 3) Recent changes in postal jurisdictions. Try searching with nearby landmarks or contact your local post office."
  },
  {
    category: "Search",
    question: "Can I search multiple PIN codes at once?",
    answer: "Currently, our system supports searching one PIN code or area at a time. However, you can quickly perform multiple searches in succession."
  },
  {
    category: "Search",
    question: "How often is the PIN code database updated?",
    answer: "Our database is updated daily to reflect any changes in India Post's records, ensuring you have access to the most current information available."
  },
  // Post Office Related
  {
    category: "Post Office",
    question: "What are the different types of post offices?",
    answer: "India Post operates several types of post offices: Head Post Offices (GPO), Sub Post Offices, and Branch Post Offices. Each serves different administrative and operational functions."
  },
  {
    category: "Post Office",
    question: "What does 'Delivery Status' mean?",
    answer: "Delivery Status indicates whether a post office provides direct delivery services. 'Delivery' means the post office handles direct mail delivery, while 'Non-Delivery' means mail is routed through another delivery post office."
  },
  {
    category: "Post Office",
    question: "How do I know which post office serves my area?",
    answer: "Enter your address or PIN code in our search tool, and we'll show you the serving post office along with its delivery jurisdiction and contact details."
  },
  {
    category: "Post Office",
    question: "What are post office working hours?",
    answer: "Most post offices operate from Monday to Saturday, typically 9:00 AM to 5:00 PM. However, specific timings may vary. GPOs and major post offices might have extended hours."
  },
  // Address Format
  {
    category: "Address",
    question: "What is the correct format for writing a PIN code?",
    answer: "Write the PIN code as a 6-digit number without any spaces, hyphens, or special characters. Place it on the last line of the address after the city/district name."
  },
  {
    category: "Address",
    question: "Where should I write the PIN code in an address?",
    answer: "The PIN code should be written on the last line of the address after the city/district name. For example: New Delhi - 110001"
  },
  {
    category: "Address",
    question: "Is it mandatory to include a PIN code in the address?",
    answer: "Yes, including a PIN code is highly recommended for faster and accurate mail delivery. Mail without PIN codes may face delays in processing and delivery."
  },
  // Technical
  {
    category: "Technical",
    question: "Is the website mobile-friendly?",
    answer: "Yes, our website is fully responsive and works well on all devices including smartphones, tablets, and desktop computers."
  },
  {
    category: "Technical",
    question: "Can I integrate this PIN code data with my website?",
    answer: "We offer API services for businesses needing PIN code validation and lookup capabilities. Contact our support team for integration details and documentation."
  },
  {
    category: "Technical",
    question: "How do I report incorrect information?",
    answer: "If you find any discrepancies in our data, please use the 'Report Error' button on the specific PIN code page or contact our support team."
  },
  {
    category: "Technical",
    question: "Is there a limit to how many searches I can perform?",
    answer: "Our free service allows unlimited searches for individual users. For high-volume commercial usage, please contact us about our API services."
  },
  {
    category: "Technical",
    question: "Do you have an offline database available?",
    answer: "We currently don't provide offline database downloads. However, our website works with minimal internet connectivity, and we offer API solutions for businesses requiring offline access."
  }
];

const FAQ: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>("General");
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});

  const categories = Array.from(new Set(faqData.map(item => item.category)));

  const toggleItem = (question: string) => {
    setOpenItems(prev => ({
      ...prev,
      [question]: !prev[question]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-50">
              Find answers to common questions about PIN codes and our services
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-4 mb-8 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            <AnimatePresence>
              {faqData
                .filter(item => item.category === activeCategory)
                .map((item, index) => (
                  <motion.div
                    key={item.question}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2, delay: index * 0.1 }}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <button
                      onClick={() => toggleItem(item.question)}
                      className="w-full px-6 py-4 text-left flex justify-between items-center"
                    >
                      <span className="font-medium text-gray-900">{item.question}</span>
                      <motion.div
                        animate={{ rotate: openItems[item.question] ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <KeyboardArrowDownIcon className="text-gray-500" />
                      </motion.div>
                    </button>
                    <AnimatePresence>
                      {openItems[item.question] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-4 text-gray-600">
                            {item.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
